import {
  computed,
  ref,
  toValue,
  watch,
  type ComputedRef,
  type MaybeRefOrGetter,
  type Ref,
} from 'vue'

import type {
  ChartFieldRole,
  ChartFieldRoleSelection,
  ChartFieldRoleSources,
  ChartType,
} from '../models/chart.types'
import { applyFieldRoleSelectionRules } from '../service/chart/fieldRoles'
import { useCustomizeStore } from '../stores/customizeStore'

import { useAccount, useWorkspace } from '@/features/account'

const CACHE_KIND = 'chartFieldRolesV1'

interface ChartFieldRoleCacheV1 {
  version: 1
  byType: Partial<Record<ChartType, ChartFieldRoleSelection>>
}

export interface UseReportChartFieldStateOptions {
  id: MaybeRefOrGetter<string>
  partitionName?: MaybeRefOrGetter<string | undefined>
  chartType: MaybeRefOrGetter<ChartType>
  roles: MaybeRefOrGetter<readonly ChartFieldRole[]>
  sources: MaybeRefOrGetter<ChartFieldRoleSources>
  /**
   * The caller's *default* dimension set, when it can change while the surface stays mounted —
   * a section's group picker rewrites it (`ReportSection.vue`). Cached assignments are scoped to
   * it, so switching the grouping hydrates from `defaultSelection` (i.e. breaks on the new group)
   * instead of restoring the previous grouping's field, and switching back restores that choice.
   */
  defaultDimensions?: MaybeRefOrGetter<readonly string[] | undefined>
  /** Seeds a chart type's very first (uncached) hydration, in place of auto-filling from `sources`
   *  — which, once `sources` offers a report's whole field universe rather than just the caller's
   *  intended defaults, would otherwise over-select whatever a multi-value role has room for. */
  defaultSelection?: MaybeRefOrGetter<ChartFieldRoleSelection | undefined>
}

export interface UseReportChartFieldState {
  selection: Ref<ChartFieldRoleSelection>
  valid: ComputedRef<boolean>
  setSelection: (selection: ChartFieldRoleSelection) => void
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function readCache(value: unknown): ChartFieldRoleCacheV1 {
  if (!isRecord(value) || value.version !== 1 || !isRecord(value.byType)) {
    return { version: 1, byType: {} }
  }
  return { version: 1, byType: value.byType as ChartFieldRoleCacheV1['byType'] }
}

/** Durable per-chart slot assignments; backend role changes are reconciled before reuse. */
export function useReportChartFieldState(
  options: UseReportChartFieldStateOptions,
): UseReportChartFieldState {
  const account = useAccount()
  const workspace = useWorkspace()
  const cache = useCustomizeStore()
  const selection = ref<ChartFieldRoleSelection>({})
  const partition = computed(() => {
    const userId = account.userId.value
    const accountId = account.accountId.value
    const workspaceId = workspace.workspaceId.value
    if (userId === undefined || accountId === undefined || workspaceId === undefined) return null
    return [
      'report-chart-fields',
      userId,
      accountId,
      String(workspaceId),
      toValue(options.partitionName) || 'default',
      toValue(options.id),
      (toValue(options.defaultDimensions) ?? []).join(','),
    ].join(':')
  })
  const resolution = computed(() =>
    applyFieldRoleSelectionRules(toValue(options.roles), toValue(options.sources), selection.value),
  )

  function persist(): void {
    if (!partition.value || !resolution.value.valid) return
    const stored = readCache(cache.getCached<unknown>(partition.value, CACHE_KIND))
    cache.setCached<ChartFieldRoleCacheV1>(partition.value, CACHE_KIND, {
      version: 1,
      byType: {
        ...stored.byType,
        [toValue(options.chartType)]: resolution.value.selection,
      },
    })
  }

  function hydrate(): void {
    const stored = partition.value
      ? readCache(cache.getCached<unknown>(partition.value, CACHE_KIND))
      : { version: 1 as const, byType: {} }
    const saved = stored.byType[toValue(options.chartType)] ?? toValue(options.defaultSelection)
    selection.value = applyFieldRoleSelectionRules(
      toValue(options.roles),
      toValue(options.sources),
      saved,
    ).selection
  }

  function reconcile(): void {
    // An empty `selection` isn't necessarily a real "nothing chosen" — a chart type whose roles
    // arrive after an async catalog load reconciles before `hydrate` ever saw real roles to seed
    // from. Falling back to `defaultSelection` there keeps that first resolution seeded the same
    // way `hydrate` seeds it, instead of auto-filling from the (now report-wide) `sources`.
    const seed =
      Object.keys(selection.value).length > 0 ? selection.value : toValue(options.defaultSelection)
    const next = applyFieldRoleSelectionRules(
      toValue(options.roles),
      toValue(options.sources),
      seed,
    ).selection
    selection.value = Object.fromEntries(
      Object.entries(next).map(([key, fields]) => [key, [...fields]]),
    )
    persist()
  }

  watch([partition, () => toValue(options.chartType)], hydrate, { immediate: true })
  watch([() => toValue(options.roles), () => toValue(options.sources)], reconcile, {
    deep: true,
  })

  return {
    selection,
    valid: computed(() => resolution.value.valid),
    setSelection: (next) => {
      selection.value = applyFieldRoleSelectionRules(
        toValue(options.roles),
        toValue(options.sources),
        next,
      ).selection
      persist()
    },
  }
}
